<template>
  <v-dialog v-model="value" max-width="650px" persistent>
    <v-card class="rescue-code-dialog">
      <v-card-title class="justify-sm-center justify-start dialog-title">
        <h2>Confirme o código de resgate</h2>
      </v-card-title>
      <v-card-text class="pa-0">
        <div class="dialog-description">
          <span>
            Para concluir a entrega deste vale-troca, é necessário informar o
            <span class="highlight">código de 6 dígitos exibido no aplicativo do cliente</span>. Isso garante que o produto será entregue à pessoa correta.
          </span>
          <span>Preencha o campo abaixo com o código fornecido pelo cliente para finalizar a entrega.</span>
        </div>
        <div class="code-inputs-container">
          <div v-for="(digit, idx) in codeDigits" :key="idx" class="code-input-wrapper">
            <mf-number-input
              :ref="'digit' + idx"
              v-model="codeDigits[idx]"
              :max="9"
              :min="0"
              :maxlength="1"
              class="code-input"
              :class="inputClass"
              :readonly="loading"
              :error="state === 'error'"
              :success="state === 'success'"
              :tabindex="idx + 1"
              hide-details
              @input="onInput(idx, $event)"
              @keydown="onKeydown(idx, $event)"
            />
          </div>
        </div>
        <div class="status-message">
          <span v-if="state === 'error'" class="error-text">Código incorreto</span>
          <span v-else-if="state === 'success'" class="success-text">Código confirmado</span>
        </div>
      </v-card-text>
      <v-card-actions class="dialog-buttons">
        <mf-button label="Cancelar" outlined color="marca-0-l10" @click="cancel" />
        <mf-button label="Confirmar entrega" color="marca-0-l10" :disabled="blockRescue" :loading="loading" @click="confirm" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { MUTATION_VALIDATE_EXCHANGE_CODE } from '@/modules/clientes/consultar-ofertas/graphql/index.js'

export default {
  name: 'RescueCodeDialog',
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    state: {
      type: String,
      default: 'initial',
    },
    documentNo: {
      type: String,
      required: true,
    },
    unit: {
      type: String,
      required: true,
    },
    blockedUntil: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      codeDigits: Array(6).fill(''),
      internalLoading: false,
      blockTimer: null,
    }
  },
  computed: {
    isCodeComplete() {
      return this.codeDigits.every((d) => d !== '' && d !== null && d !== undefined)
    },
    inputClass() {
      return {
        'input-error': this.state === 'error',
        'input-success': this.state === 'success',
      }
    },
    code() {
      return this.codeDigits.join('')
    },
    blockRescue() {
      const isBlocked = this.blockedUntil && new Date() < new Date(this.blockedUntil)
      return !this.isCodeComplete || this.state === 'success' || isBlocked
    },
  },
  watch: {
    value(val) {
      if (!val) this.reset()
    },
    state(val) {
      if (val !== 'error') this.clearError()
    },
    blockedUntil: {
      handler(newVal) {
        if (newVal) {
          if (this.blockTimer) {
            clearInterval(this.blockTimer)
          }

          this.blockTimer = setInterval(() => {
            const now = new Date()
            const blockedTime = new Date(newVal)

            if (now >= blockedTime) {
              clearInterval(this.blockTimer)
              this.blockTimer = null
              this.$forceUpdate()
            }
          }, 1000)
        } else {
          if (this.blockTimer) {
            clearInterval(this.blockTimer)
            this.blockTimer = null
          }
        }
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    if (this.blockTimer) {
      clearInterval(this.blockTimer)
      this.blockTimer = null
    }
  },
  methods: {
    onInput(idx, event) {
      if (this.codeDigits[idx] !== '' && this.codeDigits[idx] !== null && this.codeDigits[idx] !== undefined && idx < 5) {
        this.$nextTick(() => {
          const nextInput = this.$refs['digit' + (idx + 1)]
          if (nextInput && nextInput[0]) {
            nextInput[0].$el.querySelector('input').focus()
          }
        })
      }
    },
    onKeydown(idx, event) {
      if (event.key === 'Backspace' && (this.codeDigits[idx] === '' || this.codeDigits[idx] === null || this.codeDigits[idx] === undefined) && idx > 0) {
        this.$nextTick(() => {
          const prevInput = this.$refs['digit' + (idx - 1)]
          if (prevInput && prevInput[0]) {
            prevInput[0].$el.querySelector('input').focus()
          }
        })
      }
    },
    async confirm() {
      if (!this.isCodeComplete || this.internalLoading) return
      this.internalLoading = true
      this.$emit('update:loading', true)
      try {
        const { data } = await this.$apollo.mutate({
          mutation: MUTATION_VALIDATE_EXCHANGE_CODE,
          variables: {
            document_no: this.documentNo,
            code: this.code,
            unit: this.unit,
          },
        })
        const response = data?.validateExchangeCode
        if (response?.success) {
          this.$emit('confirm', this.code)
        } else {
          this.$emit('error', response?.error)
        }
      } catch (e) {
        this.$emit('error', 'Erro ao validar código')
      } finally {
        this.internalLoading = false
        this.$emit('update:loading', false)
      }
    },
    cancel() {
      this.$emit('input', false)
    },
    reset() {
      this.codeDigits = Array(6).fill('')
    },
  },
}
</script>

<style lang="scss" scoped>
.rescue-code-dialog {
  padding: 20px 16px;

  @media (min-width: 600px) {
    padding: 32px;
  }

  .dialog-title {
    padding: 0;

    h2 {
      font-size: 20px;
      font-weight: 700;
      color: #4b525a;
      text-align: center;
      margin-bottom: 16px;

      @media (min-width: 600px) {
        font-size: 26px;
        margin-bottom: 24px;
      }
    }
  }
  .dialog-description {
    display: flex;
    flex-direction: column;
    font-size: 13px;
    color: $cinza-0;
    margin-bottom: 40px;
    font-weight: 700;
    gap: 16px;

    @media (min-width: 600px) {
      gap: 8px;
      font-size: 14px;
      margin-bottom: 32px;
    }

    .highlight {
      color: $marca-1;
    }
  }
  .code-inputs-container {
    display: flex;
    justify-content: center;
    gap: 8px;

    @media (min-width: 600px) {
      gap: 16px;
    }
  }
  .status-message {
    text-align: center;
    margin-top: 16px;

    @media (min-width: 600px) {
      margin-top: 16px;
    }
  }
  .dialog-buttons {
    margin-top: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0;

    @media (max-width: 600px) {
      ::v-deep .v-btn,
      .v-badge {
        width: 100%;
      }
    }

    @media (min-width: 600px) {
      margin-top: 32px;
      flex-direction: row;
      justify-content: flex-end;
      gap: 20px;
    }
  }
  .code-input {
    width: 100%;
    height: 100%;
    font-size: 18px;
    text-align: center;
    border-radius: 8px;
    border: 1px solid $cinza-l10;
    box-shadow: 0px 3px 6px 0px #0000001a;
    background-color: #f7f7f7;

    @media (min-width: 600px) {
      width: 56px;
      font-size: 20px;
    }

    &.input-error {
      border-color: $vermelho-0 !important;

      ::v-deep input {
        color: $vermelho-0 !important;
      }
    }

    &.input-success {
      border-color: $verde-0 !important;

      ::v-deep input {
        color: $verde-0 !important;
      }
    }

    ::v-deep input {
      text-align: center;
    }
  }
  .error-text {
    color: $vermelho-0;
    font-size: 12px;
    font-weight: 700;
  }
  .success-text {
    color: $verde-0;
    font-size: 12px;
    font-weight: 700;
  }
}
</style>
